import React, { useState } from 'react';
import { Package, Loader2, Plus, Check, Search, ChevronRight, MapPin, X, Calendar, Users, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

const USER_TYPES = [
  { value: 'SELLER', label: 'Vendeur' },
  { value: 'PHARMACIST', label: 'Pharmacien' },
  { value: 'PARAPHARMACIST', label: 'Parapharmacien' },
];

const STATUS_LABELS = {
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-800' },
  INACTIVE: { label: 'Inactive', color: 'bg-gray-100 text-gray-600' },
  PENDING: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  EXPIRED: { label: 'Expirée', color: 'bg-red-100 text-red-700' },
};

const Step2ProductsCommissions = ({
  selectedLaboratory,
  selectedProducts,
  promotions,
  regions = [],
  existingPromotions = [],
  isLoadingProducts,
  onProductToggle,
  onPromotionUpdate,
  onCreateProduct,
}) => {
  const [productSearch, setProductSearch] = useState('');


  const products = selectedLaboratory?.products || [];
  const filteredProducts = products.filter(
    p => !productSearch || p.name?.toLowerCase().includes(productSearch.toLowerCase())
  );

  const hasPrixPGHT = (product) => product?.prixPGHT != null && product.prixPGHT !== '';

  // Mazid gain in DH = prixPGHT * mazidPct / 100  (strictly on PGHT, no price fallback)
  const getMazidGainDH = (promo, product) => {
    const pct = parseFloat(promo?.commissionPercentage);
    if (!pct || !hasPrixPGHT(product)) return null;
    return (parseFloat(product.prixPGHT) * pct / 100).toFixed(2);
  };

  const totalSellerCommission = Object.values(promotions).reduce((sum, promo) => {
    return sum + (parseFloat(promo?.commissionAmount) || 0);
  }, 0);

  return (
    <div className="overflow-y-auto flex-1 min-h-0">
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Produits & Commissions</h2>
        <p className="text-sm text-gray-500">
          Sélectionnez les produits et définissez les commissions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Left: Product catalogue ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">
              Catalogue
              {products.length > 0 && (
                <span className="ml-1.5 text-xs font-normal text-gray-400">
                  ({products.length})
                </span>
              )}
            </span>
            <button
              onClick={onCreateProduct}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              <Plus size={13} /> Nouveau produit
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* List */}
          {isLoadingProducts ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
              <span className="text-sm text-gray-400">Chargement...</span>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-400">
              {productSearch
                ? `Aucun résultat pour « ${productSearch} »`
                : 'Aucun produit disponible'}
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-0.5">
              {filteredProducts.map(product => {
                const isSelected = selectedProducts.some(p => p.id === product.id);
                return (
                  <button
                    key={product.id}
                    onClick={() => onProductToggle(product)}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-100 hover:border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-600' : 'bg-gray-100'
                        }`}
                    >
                      {isSelected ? (
                        <Check size={14} className="text-white" />
                      ) : (
                        <Package size={14} className="text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                      {product.category && (
                        <p className="text-xs text-gray-400 truncate">{product.category}</p>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-gray-700 flex-shrink-0">
                      {product.price} DH
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selectedProducts.length > 0 && (
            <p className="text-xs text-gray-400 text-center mt-3">
              {selectedProducts.length} produit{selectedProducts.length > 1 ? 's' : ''} sélectionné
              {selectedProducts.length > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* ── Right: Commission config ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">Commissions</span>
            {selectedProducts.length > 0 && totalSellerCommission > 0 && (
              <span className="text-xs text-green-600 font-medium">
                Vendeur ≈ {totalSellerCommission.toFixed(2)} DH / scan
              </span>
            )}
          </div>

          {selectedProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] border-2 border-dashed border-gray-200 rounded-2xl">
              <ChevronRight size={22} className="text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">Sélectionnez des produits</p>
              <p className="text-xs text-gray-300 mt-1">Les commissions apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-0.5">
              {selectedProducts.map((product) => {
                const promo = promotions[product.id] || {};
                const mazidDH = getMazidGainDH(promo, product);

                return (
                  <div
                    key={product.id}
                    className="border border-gray-100 rounded-2xl bg-white p-4"
                  >
                    {/* Product header */}
                    <div className="flex items-center gap-2 min-w-0 mb-3">
                      <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {product.name}
                      </p>
                      {hasPrixPGHT(product) ? (
                        <span className="text-xs text-blue-600 font-medium flex-shrink-0">PGHT: {product.prixPGHT} DH</span>
                      ) : (
                        <span className="text-xs text-orange-500 font-medium flex-shrink-0 flex items-center gap-0.5">
                          <AlertTriangle size={11} /> PGHT manquant
                        </span>
                      )}
                    </div>

                    {/* Commission inputs: Vendeur DH + Mazid % */}
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="bg-green-50 p-2.5 rounded-lg border border-green-100">
                        <label className="text-xs font-medium text-green-700 block mb-1">
                          Gain Vendeur (DH)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={promo.commissionAmount ?? ''}
                          onChange={e =>
                            onPromotionUpdate(product.id, {
                              ...promo,
                              commissionAmount: parseFloat(e.target.value) || 0,
                              commissionType: 'FIXED_PERCENTAGE',
                            })
                          }
                          className="w-full px-2 py-1.5 text-sm border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="bg-blue-50 p-2.5 rounded-lg border border-blue-100">
                        <label className="text-xs font-medium text-blue-700 block mb-1">
                          Gain Mazid (% PGHT)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={promo.commissionPercentage ?? ''}
                          onChange={e =>
                            onPromotionUpdate(product.id, {
                              ...promo,
                              commissionPercentage: parseFloat(e.target.value) || 0,
                              commissionType: 'FIXED_PERCENTAGE',
                            })
                          }
                          className="w-full px-2 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          placeholder="0.0"
                        />
                        {mazidDH ? (
                          <p className="text-xs text-blue-600 font-medium mt-1">≈ {mazidDH} DH</p>
                        ) : !hasPrixPGHT(product) && promo.commissionPercentage > 0 ? (
                          <p className="text-xs text-orange-500 mt-1">Ajoutez le PGHT pour calculer</p>
                        ) : null}
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Date de début</label>
                        <input
                          type="date"
                          value={promo.startDate ?? ''}
                          onChange={e =>
                            onPromotionUpdate(product.id, { ...promo, startDate: e.target.value })
                          }
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Date de fin</label>
                        <input
                          type="date"
                          value={promo.endDate ?? ''}
                          onChange={e =>
                            onPromotionUpdate(product.id, { ...promo, endDate: e.target.value })
                          }
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Overlap warning */}
                    {promo.startDate && promo.endDate && (() => {
                      const newStart = new Date(promo.startDate);
                      const newEnd = new Date(promo.endDate);
                      const conflict = existingPromotions.find(ep => {
                        if (!ep.products?.some(p => p.id === product.id)) return false;
                        const epStart = new Date(ep.startDate);
                        const epEnd = new Date(ep.endDate);
                        return newStart <= epEnd && newEnd >= epStart;
                      });
                      return conflict ? (
                        <div className="flex items-start gap-1.5 mb-2 text-red-600">
                          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                          <p className="text-xs">
                            Conflit avec &laquo;{conflict.name}&raquo; ({format(new Date(conflict.startDate), 'dd/MM/yy')} – {format(new Date(conflict.endDate), 'dd/MM/yy')})
                          </p>
                        </div>
                      ) : null;
                    })()}

                    {/* Quantity */}
                    <div className="mb-2">
                      <label className="text-xs text-gray-400 block mb-1">Quantité QR codes</label>
                      <input
                        type="number"
                        min="1"
                        step="100"
                        value={promo.quantity ?? 1000}
                        onChange={e =>
                          onPromotionUpdate(product.id, {
                            ...promo,
                            quantity: parseInt(e.target.value) || 1000,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Region restrictions */}
                    {regions.length > 0 && (
                      <div className="mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs text-gray-400 flex items-center gap-1">
                            <MapPin size={10} /> Régions cibles
                          </label>
                          {(promo.regionIds?.length ?? 0) === 0 && (
                            <span className="text-xs text-blue-500 font-medium">Toutes les régions</span>
                          )}
                        </div>
                        {/* Selected region chips */}
                        {(promo.regionIds?.length ?? 0) > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {promo.regionIds.map(rid => {
                              const r = regions.find(r => r.id === rid);
                              return r ? (
                                <span key={rid} className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full">
                                  {r.nomRegion}
                                  <button
                                    type="button"
                                    onClick={() => onPromotionUpdate(product.id, {
                                      ...promo,
                                      regionIds: promo.regionIds.filter(id => id !== rid),
                                    })}
                                    className="text-blue-400 hover:text-blue-600"
                                  >
                                    <X size={9} />
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                        {/* Region dropdown */}
                        <select
                          value=""
                          onChange={e => {
                            const rid = e.target.value;
                            if (!rid) return;
                            const current = promo.regionIds ?? [];
                            if (!current.includes(rid)) {
                              onPromotionUpdate(product.id, { ...promo, regionIds: [...current, rid] });
                            }
                          }}
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-500"
                        >
                          <option value="">+ Ajouter une région...</option>
                          {regions
                            .filter(r => !(promo.regionIds ?? []).includes(r.id))
                            .map(r => (
                              <option key={r.id} value={r.id}>{r.nomRegion}</option>
                            ))}
                        </select>
                      </div>
                    )}

                    {/* UserType restriction */}
                    <div className="mb-2">
                      <div className="flex items-center gap-1 mb-1">
                        <Users size={10} className="text-gray-400" />
                        <label className="text-xs text-gray-400">Types autorisés</label>
                        {(promo.allowedUserTypes?.length ?? 0) === 0 && (
                          <span className="text-xs text-blue-500 font-medium ml-auto">Tous</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {USER_TYPES.map(({ value, label }) => {
                          const checked = (promo.allowedUserTypes ?? []).includes(value);
                          return (
                            <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                className="h-3 w-3 text-blue-600 border-gray-300 rounded"
                                checked={checked}
                                onChange={() => {
                                  const current = promo.allowedUserTypes ?? [];
                                  onPromotionUpdate(product.id, {
                                    ...promo,
                                    allowedUserTypes: checked
                                      ? current.filter(t => t !== value)
                                      : [...current, value],
                                  });
                                }}
                              />
                              <span className="text-xs text-gray-600">{label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Preview */}
                    {(promo.commissionAmount > 0 || mazidDH) && (
                      <div className="flex justify-between text-xs pt-2 border-t border-gray-50 gap-2">
                        {promo.commissionAmount > 0 && (
                          <span className="text-green-600 font-semibold">
                            Vendeur +{parseFloat(promo.commissionAmount).toFixed(2)} DH
                          </span>
                        )}
                        {mazidDH && (
                          <span className="text-blue-600 font-semibold">
                            Mazid +{mazidDH} DH
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step2ProductsCommissions;
